import React from 'react';
import { makeStyles } from '@material-ui/core';
import { COLORS } from './data';

const useStyles = makeStyles(() => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
    marginBottom: 12,
    '@media (min-width: 900px)': {
      gridTemplateColumns: '1.5fr 1fr 0.9fr 0.9fr',
      gap: 14,
    },
    '& > *': { minWidth: 0 },
  },
  card: {
    background: 'white',
    borderRadius: 12,
    padding: 14,
    border: '1px solid rgba(0,0,0,0.06)',
  },
  hero: {
    background: COLORS.ink,
    color: 'white',
    gridColumn: '1 / -1',
    padding: 18,
    position: 'relative',
    overflow: 'hidden',
    '@media (min-width: 900px)': { gridColumn: 'auto' },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: -30,
      right: -30,
      width: 120,
      height: 120,
      background: COLORS.blue,
      borderRadius: '50%',
      opacity: 0.12,
    },
  },
  label: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: COLORS.slate,
    marginBottom: 6,
  },
  labelHero: { color: 'rgba(255,255,255,0.5)' },
  value: {
    fontSize: 24,
    fontWeight: 800,
    color: COLORS.ink,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '@media (min-width: 900px)': { fontSize: 22 },
  },
  valueHero: {
    color: 'white',
    fontSize: 28,
    '@media (min-width: 900px)': { fontSize: 30 },
  },
  delta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    fontWeight: 600,
    padding: '3px 8px',
    borderRadius: 20,
    marginTop: 6,
    whiteSpace: 'nowrap',
  },
  up: { background: 'rgba(33,170,157,0.12)', color: COLORS.success },
  neutral: { background: 'rgba(63,99,114,0.1)', color: COLORS.slate },
  sub: {
    fontSize: 11,
    color: COLORS.slate,
    marginTop: 5,
    lineHeight: 1.4,
  },
  subHero: { color: 'rgba(255,255,255,0.45)' },
  progressBar: {
    height: 4,
    background: COLORS.mist,
    borderRadius: 10,
    overflow: 'hidden',
    marginTop: 8,
  },
  progressFill: {
    height: '100%',
    background: COLORS.blue,
    borderRadius: 10,
  },
  chip: {
    display: 'inline-block',
    fontSize: 10.5,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 20,
    marginTop: 4,
    marginRight: 3,
  },
  chipDanger: { background: '#FEF0F0', color: COLORS.danger },
  chipWarn: { background: '#FFF6EC', color: COLORS.warning },
  valueDanger: { color: COLORS.danger },
  valueSuccess: { color: COLORS.success },
}));

export default function KpiGrid({ kpis }) {
  const classes = useStyles();
  const {
    ventasHoy, esteMes, alertasStock, margen,
  } = kpis;

  return (
    <div className={classes.grid}>
      {/* Hero — Ventas de hoy */}
      <div className={`${classes.card} ${classes.hero}`}>
        <div className={`${classes.label} ${classes.labelHero}`}>Ventas de hoy</div>
        <div className={`${classes.value} ${classes.valueHero}`}>{ventasHoy.value}</div>
        <div>
          <span className={`${classes.delta} ${classes.up}`}>↑ {ventasHoy.delta}</span>
        </div>
        <div className={`${classes.sub} ${classes.subHero}`} style={{ marginTop: 8 }}>
          {ventasHoy.sub}
        </div>
      </div>

      {/* Este mes */}
      <div className={classes.card}>
        <div className={classes.label}>Este mes</div>
        <div className={classes.value}>{esteMes.value}</div>
        <div>
          <span className={`${classes.delta} ${classes.up}`}>↑ {esteMes.delta}</span>
        </div>
        <div className={classes.progressBar}>
          <div className={classes.progressFill} style={{ width: `${esteMes.progress}%` }} />
        </div>
        <div className={classes.sub}>{esteMes.progress}% · Meta {esteMes.meta}</div>
      </div>

      {/* Alertas de stock */}
      <div className={classes.card}>
        <div className={classes.label}>Alertas de stock</div>
        <div className={`${classes.value} ${classes.valueDanger}`}>{alertasStock.value}</div>
        <div className={classes.sub}>productos bajo mínimo</div>
        <div>
          <span className={`${classes.chip} ${classes.chipDanger}`}>{alertasStock.sinStock} sin stock</span>
          <span className={`${classes.chip} ${classes.chipWarn}`}>{alertasStock.bajoMinimo} bajo mínimo</span>
        </div>
      </div>

      {/* Margen estimado */}
      <div className={classes.card}>
        <div className={classes.label}>Margen estimado</div>
        <div className={`${classes.value} ${classes.valueSuccess}`}>{margen.value}</div>
        <div>
          <span className={`${classes.delta} ${classes.neutral}`}>{margen.delta}</span>
        </div>
        <div className={classes.sub}>{margen.sub}</div>
      </div>
    </div>
  );
}
